import React, { useEffect, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentTableData, selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { selectCalendarStats, selectFocusCalendarView } from '@/redux/selectors/focusCalendarSelectors';
import { getCalendarStats } from '@/redux/slices/focusCalendarSlice';
import ColumnHeader from './CalendarSharedComponents/ColumnHeader';
import WeeklyView from './CalendarComponents/WeeklyView';
import MonthlyView from './CalendarComponents/MonthlyView';
import DailyView from '@/components/_common/Table/Calendar/CalendarComponents/DailyView';
import StatusPanel from '@/components/_common/Table/Calendar/CalendarComponents/StatusPanel';
import LoadingBar from '../../LoadingBar';
import { ApiHourlyData } from '@/components/_common/Table/Calendar/CalendarComponents/types';
import { CALENDAR_CELLS_GAP } from '@/components/_common/Table/Calendar/constants';
import { CALENDAR_START_DATE_FORMAT } from '@/_utils/date';

const CalendarContainer = ({ loading = false }: { loading: boolean }) => {
  const dispatch = useAppDispatch();
  const apiData: ApiHourlyData[] = useAppSelector(selectCurrentTableData);
  const view = useAppSelector(selectFocusCalendarView);
  const appliedFilters = useAppSelector(selectDataTableFilters);
  const stats = useAppSelector(selectCalendarStats);
  const calendarContainerRef = useRef<HTMLDivElement | null>(null);
  const isGridView = ['month', 'week'].includes(view);
  const hasAvails = apiData.length > 0;

  useEffect(() => {
    const promise = dispatch(getCalendarStats({ appliedFilters }));

    return () => {
      promise.abort();
    };
  }, [appliedFilters]);

  const totalCountByDay = useMemo(
    () =>
      stats?.reduce<Record<string, number>>((acc, stat) => {
        acc[format(new Date(stat.day_start), CALENDAR_START_DATE_FORMAT)] = stat.stats.total_count;
        return acc;
      }, {}),
    [stats]
  );

  const Wrapper = isGridView ? GridWrapper : SimpleWrapper;

  return (
    <>
      {loading ? (
        <StyledLoadingBar loading={loading} />
      ) : (
        <>
          <StatusPanel stats={stats} />
          <Wrapper ref={calendarContainerRef}>
            {hasAvails &&
              isGridView &&
              ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((header) => <ColumnHeader key={header} label={header} />)}
            {view === 'day' && <DailyView />}
            {view === 'week' && <WeeklyView calendarContainer={calendarContainerRef.current} totalCountByDay={totalCountByDay} />}
            {view === 'month' && <MonthlyView calendarContainer={calendarContainerRef.current} totalCountByDay={totalCountByDay} />}
          </Wrapper>
        </>
      )}
    </>
  );
};

export default CalendarContainer;

const SimpleWrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-bottom-left-radius: ${({ theme }) => `${theme.shape.borderRadius * 2}px`};
  border-bottom-right-radius: ${({ theme }) => `${theme.shape.borderRadius * 2}px`};
`;

const GridWrapper = styled.div`
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(7, minmax(120px, 1fr));
  gap: ${CALENDAR_CELLS_GAP}px;
  align-items: stretch;
`;

const StyledLoadingBar = styled(LoadingBar)`
  position: absolute;
  z-index: 1;
`;
